import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import StandardFilterPanel from '../../components/hr/StandardFilterPanel';
import { Card, Button, Table, Space, Tag, message, Tooltip, Avatar, Modal, Dropdown } from 'antd';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import {
  PlusOutlined,
  ReloadOutlined,
  EyeOutlined,
  EditOutlined,
  CustomerServiceOutlined,
  DownOutlined,
  DeleteOutlined,
  SettingOutlined,
  FormOutlined,
} from '@ant-design/icons';
import { getLast30DaysRange } from '../../utils/dateUtils';
import { ticketsApi } from '../../utils/api';
import { getImagePath } from '../../utils/imageHelper';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';
import { useListReturnState } from '../../hooks/useListReturnState';
import { PANEL_PRIMARY_BTN_CLASS, BULK_ACTION_BAR_CLASS, BULK_ACTION_BAR_TEXT_CLASS } from '../../constants/ui';

const statusMeta = {
  open: { text: 'باز', color: 'red' },
  pending: { text: 'در انتظار', color: 'gold' },
  resolved: { text: 'حل‌شده', color: 'green' },
  closed: { text: 'بسته‌شده', color: 'blue' },
};

const priorityMeta = {
  low: { text: 'پایین', color: 'default' },
  medium: { text: 'متوسط', color: 'blue' },
  high: { text: 'بالا', color: 'orange' },
  urgent: { text: 'فوری', color: 'red' },
};

const toFilterDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value.replace(/\//g, '-');
  if (value?.format && typeof value.format === 'function') return value.format('jYYYY/MM/DD').replace(/\//g, '-');
  return null;
};

const collectAgentsFromGroups = (groups = []) => {
  const map = new Map();
  (groups || []).forEach((g) => {
    (g.enabled_agents || []).forEach((ea) => {
      const u = ea.user;
      if (u?.id != null) map.set(u.id, u);
    });
  });
  return Array.from(map.values());
};

const Tickets = () => {
  const navigate = useNavigate();
  const {
    canAdd,
    canEdit,
    canDelete,
    canManageAgent,
    canEditRecord,
    canDeleteRecord,
  } = useResourcePermissions('tickets');
  const canEditStatus = canEdit;
  const defaultRange = useMemo(() => getLast30DaysRange('jYYYY-jMM-jDD'), []);

  const [filterValues, setFilterValues] = useState({
    search: '',
    startDate: defaultRange.startDate,
    endDate: defaultRange.endDate,
    status: 'all',
    group_id: 'all',
    agent_id: 'all',
    priority: 'all',
    channel_id: 'all',
    type_id: 'all',
    tag_id: 'all',
    project_id: 'all',
  });

  const [loading, setLoading] = useState(false);
  const [countLoading, setCountLoading] = useState(false);
  const [countStats, setCountStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, per_page: 10, total: 0, last_page: 1 });
  const [filterOptions, setFilterOptions] = useState({
    channels: [],
    groups: [],
    types: [],
    tags: [],
    projects: [],
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [statusLoadingId, setStatusLoadingId] = useState(null);

  const captureCurrentListState = useCallback(
    () => ({
      filters: filterValues,
      pagination: {
        current_page: pagination.current_page,
        per_page: pagination.per_page,
      },
    }),
    [filterValues, pagination.current_page, pagination.per_page],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.filters) setFilterValues(restored.filters);
    if (restored.pagination) {
      setPagination((prev) => ({
        ...prev,
        current_page: restored.pagination.current_page || 1,
        per_page: restored.pagination.per_page || prev.per_page,
      }));
    }
  });

  const agentOptions = useMemo(() => {
    const groups = filterOptions.groups || [];
    if (filterValues.group_id === 'all') return collectAgentsFromGroups(groups);
    const g = groups.find((x) => String(x.id) === String(filterValues.group_id));
    return collectAgentsFromGroups(g ? [g] : []);
  }, [filterOptions.groups, filterValues.group_id]);

  const fetchTickets = async (page = pagination.current_page, perPage = pagination.per_page) => {
    setLoading(true);
    try {
      const res = await ticketsApi.getAll({
        startDate: toFilterDate(filterValues.startDate),
        endDate: toFilterDate(filterValues.endDate),
        status: filterValues.status,
        group_id: filterValues.group_id,
        agent_id: filterValues.agent_id,
        priority: filterValues.priority,
        channel_id: filterValues.channel_id,
        type_id: filterValues.type_id,
        tag_id: filterValues.tag_id,
        project_id: filterValues.project_id,
        search: filterValues.search || '',
        page,
        per_page: perPage,
      });
      setTickets(res?.tickets || []);
      setPagination(res?.pagination || { current_page: page, per_page: perPage, total: 0, last_page: 1 });
      setFilterOptions(
        res?.filters || {
          channels: [],
          groups: [],
          types: [],
          tags: [],
          projects: [],
        }
      );
    } catch (error) {
      message.error('خطا در دریافت تیکت‌ها');
    } finally {
      setLoading(false);
    }
  };

  const fetchCounts = async () => {
    setCountLoading(true);
    try {
      const res = await ticketsApi.refreshCount({
        startDate: toFilterDate(filterValues.startDate),
        endDate: toFilterDate(filterValues.endDate),
        tag_id: filterValues.tag_id,
        agent_id: filterValues.agent_id,
        priority: filterValues.priority,
        channel_id: filterValues.channel_id,
        type_id: filterValues.type_id,
        group_id: filterValues.group_id,
        project_id: filterValues.project_id,
      });
      const d = res?.data;
      if (d) setCountStats(d);
    } catch (error) {
      setCountStats(null);
    } finally {
      setCountLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets(pagination.current_page, pagination.per_page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current_page, pagination.per_page]);

  useEffect(() => {
    fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination((prev) => ({ ...prev, current_page: 1 }));
      fetchTickets(1, pagination.per_page);
      fetchCounts();
    }, 450);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues]);

  useEffect(() => {
    if (filterValues.group_id !== 'all' && filterValues.agent_id !== 'all') {
      const allowed = new Set(agentOptions.map((a) => String(a.id)));
      if (!allowed.has(String(filterValues.agent_id))) {
        setFilterValues((prev) => ({ ...prev, agent_id: 'all' }));
      }
    }
  }, [filterValues.group_id, filterValues.agent_id, agentOptions]);

  useEffect(() => {
    setSelectedRowKeys([]);
  }, [filterValues]);

  const handleFilterChange = (key, value) => {
    const normalized = value === null || value === undefined || value === '' ? 'all' : value;
    setFilterValues((prev) => {
      const next = { ...prev, [key]: normalized };
      if (key === 'group_id') next.agent_id = 'all';
      return next;
    });
  };

  const handleSearchChange = (value) => {
    setFilterValues((prev) => ({ ...prev, search: value ?? '' }));
  };

  const handleResetFilters = () => {
    const dr = getLast30DaysRange('jYYYY-jMM-jDD');
    setFilterValues({
      search: '',
      startDate: dr.startDate,
      endDate: dr.endDate,
      status: 'all',
      group_id: 'all',
      agent_id: 'all',
      priority: 'all',
      channel_id: 'all',
      type_id: 'all',
      tag_id: 'all',
      project_id: 'all',
    });
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleRowStatusChange = async (ticketId, newStatus) => {
    setStatusLoadingId(ticketId);
    try {
      await ticketsApi.changeStatus(ticketId, newStatus);
      message.success('وضعیت تیکت بروزرسانی شد');
      fetchTickets();
      fetchCounts();
    } catch (error) {
      message.error('خطا در تغییر وضعیت تیکت');
    } finally {
      setStatusLoadingId(null);
    }
  };

  const handleBulkDelete = () => {
    Modal.confirm({
      title: <span style={{ fontFamily: 'CustomFont' }}>حذف تیکت‌ها</span>,
      content: (
        <span style={{ fontFamily: 'CustomFont' }}>
          آیا از حذف {selectedRowKeys.length} تیکت انتخاب‌شده اطمینان دارید؟
        </span>
      ),
      okText: 'بله، حذف کن',
      cancelText: 'لغو',
      okButtonProps: {
        danger: true,
        style: { backgroundColor: '#dc2626', borderColor: '#dc2626', color: '#fff' },
      },
      onOk: async () => {
        setBulkActionLoading(true);
        try {
          await ticketsApi.applyQuickAction('delete', selectedRowKeys);
          message.success('تیکت‌ها حذف شدند');
          setSelectedRowKeys([]);
          fetchTickets();
          fetchCounts();
        } catch (error) {
          message.error('خطا در حذف تیکت‌ها');
        } finally {
          setBulkActionLoading(false);
        }
      },
    });
  };

  const handleBulkStatusChange = (newStatus) => {
    const label = statusMeta[newStatus]?.text || newStatus;
    Modal.confirm({
      title: <span style={{ fontFamily: 'CustomFont' }}>تغییر وضعیت تیکت‌ها</span>,
      content: (
        <span style={{ fontFamily: 'CustomFont' }}>
          آیا از تغییر وضعیت {selectedRowKeys.length} تیکت به «{label}» اطمینان دارید؟
        </span>
      ),
      okText: 'بله',
      cancelText: 'لغو',
      okButtonProps: {
        style: { backgroundColor: '#2563eb', borderColor: '#2563eb', color: '#fff' },
      },
      onOk: async () => {
        setBulkActionLoading(true);
        try {
          await ticketsApi.applyQuickAction('change-status', selectedRowKeys, newStatus);
          message.success('وضعیت تیکت‌ها بروزرسانی شد');
          setSelectedRowKeys([]);
          fetchTickets();
          fetchCounts();
        } catch (error) {
          message.error('خطا در تغییر وضعیت');
        } finally {
          setBulkActionLoading(false);
        }
      },
    });
  };

  const bulkActionMenuItems = [
    ...(canDelete
      ? [
          {
            key: 'delete',
            label: <span style={{ fontFamily: 'CustomFont' }}>حذف انتخاب‌شده‌ها</span>,
            icon: <DeleteOutlined />,
            danger: true,
            onClick: handleBulkDelete,
          },
          { type: 'divider' },
        ]
      : []),
    ...(canEditStatus
      ? Object.keys(statusMeta).map((key) => ({
          key: `status-${key}`,
          label: <span style={{ fontFamily: 'CustomFont' }}>تغییر به «{statusMeta[key].text}»</span>,
          onClick: () => handleBulkStatusChange(key),
        }))
      : []),
  ];

  const statusQuickOptions = useMemo(
    () => Object.entries(statusMeta).map(([value, meta]) => ({ value, label: meta.text })),
    []
  );

  const priorityQuickOptions = useMemo(
    () => Object.entries(priorityMeta).map(([value, meta]) => ({ value, label: meta.text })),
    []
  );

  const groupOptions = useMemo(
    () =>
      (filterOptions.groups || []).map((g) => ({
        value: g.id,
        label: g.group_name || g.name,
      })),
    [filterOptions.groups]
  );

  const agentSelectOptions = useMemo(
    () => agentOptions.map((a) => ({ value: a.id, label: a.name })),
    [agentOptions]
  );

  const channelOptions = useMemo(
    () =>
      (filterOptions.channels || []).map((c) => ({
        value: c.id,
        label: c.channel_name || c.name,
      })),
    [filterOptions.channels]
  );

  const typeOptions = useMemo(
    () =>
      (filterOptions.types || []).map((t) => ({
        value: t.id,
        label: t.type || t.name,
      })),
    [filterOptions.types]
  );

  const tagOptions = useMemo(
    () =>
      (filterOptions.tags || []).map((t) => ({
        value: t.id,
        label: t.tag_name || t.name,
      })),
    [filterOptions.tags]
  );

  const projectOptions = useMemo(
    () =>
      (filterOptions.projects || []).map((p) => ({
        value: p.id,
        label: p.project_name || p.name,
      })),
    [filterOptions.projects]
  );

  const quickFilters = [
    {
      key: 'status',
      type: 'select',
      label: 'وضعیت',
      placeholder: 'همه وضعیت‌ها',
      value: filterValues.status === 'all' ? undefined : filterValues.status,
      options: statusQuickOptions,
      showSearch: false,
    },
    {
      key: 'priority',
      type: 'select',
      label: 'اولویت',
      placeholder: 'همه اولویت‌ها',
      value: filterValues.priority === 'all' ? undefined : filterValues.priority,
      options: priorityQuickOptions,
      showSearch: false,
    },
  ];

  const advancedFilters = [
    {
      key: 'startDate',
      type: 'date',
      label: 'از تاریخ',
      placeholder: 'از تاریخ',
      value: filterValues.startDate,
    },
    {
      key: 'endDate',
      type: 'date',
      label: 'تا تاریخ',
      placeholder: 'تا تاریخ',
      value: filterValues.endDate,
    },
    {
      key: 'group_id',
      type: 'select',
      label: 'گروه',
      placeholder: 'همه گروه‌ها',
      value: filterValues.group_id === 'all' ? undefined : filterValues.group_id,
      options: groupOptions,
      showSearch: true,
    },
    {
      key: 'agent_id',
      type: 'select',
      label: 'پشتیبان',
      placeholder: 'همه پشتیبان‌ها',
      value: filterValues.agent_id === 'all' ? undefined : filterValues.agent_id,
      options: agentSelectOptions,
      showSearch: true,
    },
    {
      key: 'channel_id',
      type: 'select',
      label: 'کانال',
      placeholder: 'همه کانال‌ها',
      value: filterValues.channel_id === 'all' ? undefined : filterValues.channel_id,
      options: channelOptions,
      showSearch: true,
    },
    {
      key: 'type_id',
      type: 'select',
      label: 'نوع تیکت',
      placeholder: 'همه انواع',
      value: filterValues.type_id === 'all' ? undefined : filterValues.type_id,
      options: typeOptions,
      showSearch: true,
    },
    {
      key: 'tag_id',
      type: 'select',
      label: 'تگ',
      placeholder: 'همه تگ‌ها',
      value: filterValues.tag_id === 'all' ? undefined : filterValues.tag_id,
      options: tagOptions,
      showSearch: true,
    },
    {
      key: 'project_id',
      type: 'select',
      label: 'پروژه',
      placeholder: 'همه پروژه‌ها',
      value: filterValues.project_id === 'all' ? undefined : filterValues.project_id,
      options: projectOptions,
      showSearch: true,
    },
  ];

  const columns = [
    {
      title: 'شماره',
      dataIndex: 'ticket_number',
      key: 'ticket_number',
      width: 90,
      render: (text) => <span style={{ fontFamily: 'CustomFont', fontWeight: 600 }}>{text}</span>,
    },
    {
      title: 'موضوع',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
      render: (text) => <span style={{ fontFamily: 'CustomFont' }}>{text || '—'}</span>,
    },
    {
      title: 'درخواست‌کننده',
      key: 'requester',
      render: (_, record) => (
        <Space>
          <Avatar src={getImagePath(record.requester?.image_url)} size="small">
            {(record.requester?.name || '?').charAt(0)}
          </Avatar>
          <span style={{ fontFamily: 'CustomFont' }}>{record.requester?.name || '—'}</span>
        </Space>
      ),
    },
    {
      title: 'پشتیبان',
      key: 'agent',
      render: (_, record) =>
        record.agent ? (
          <Space>
            <Avatar src={getImagePath(record.agent?.image_url)} size="small">
              {(record.agent?.name || '?').charAt(0)}
            </Avatar>
            <span style={{ fontFamily: 'CustomFont' }}>{record.agent.name}</span>
          </Space>
        ) : (
          '—'
        ),
    },
    {
      title: 'اولویت',
      dataIndex: 'priority',
      key: 'priority',
      width: 110,
      render: (value) => {
        const meta = priorityMeta[value] || { text: value, color: 'default' };
        return (
          <Tag color={meta.color} style={{ fontFamily: 'CustomFont' }}>
            {meta.text}
          </Tag>
        );
      },
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (value, record) => {
        const meta = statusMeta[value] || { text: value, color: 'default' };
        if (!canEditRecord(record)) {
          return (
            <Tag color={meta.color} style={{ fontFamily: 'CustomFont' }}>
              {meta.text}
            </Tag>
          );
        }
        const statusMenuItems = Object.keys(statusMeta).map((key) => ({
          key,
          label: <span style={{ fontFamily: 'CustomFont' }}>{statusMeta[key].text}</span>,
          onClick: () => handleRowStatusChange(record.id, key),
        }));
        return (
          <Dropdown menu={{ items: statusMenuItems }} trigger={['click']}>
            <Button type="text" loading={statusLoadingId === record.id} style={{ padding: 0, height: 'auto' }}>
              <Tag color={meta.color} style={{ fontFamily: 'CustomFont', cursor: 'pointer' }}>
                {meta.text}
              </Tag>
            </Button>
          </Dropdown>
        );
      },
    },
    {
      title: 'تاریخ',
      dataIndex: 'created_on',
      key: 'created_on',
      width: 160,
      render: (text) => <span style={{ fontFamily: 'CustomFont' }}>{text || '—'}</span>,
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: canDelete ? 130 : 90,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="مشاهده">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() =>
                navigateToDetail(`/tickets/${record.ticket_number}`, {}, {
                  listPath: '/tickets',
                  listLabel: 'تیکت‌ها',
                  entityLabel: record.subject || record.ticket_number,
                })
              }
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            />
          </Tooltip>
          <Tooltip title="ویرایش">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() =>
                navigateToDetail(`/tickets/${record.ticket_number}`, {}, {
                  listPath: '/tickets',
                  listLabel: 'تیکت‌ها',
                  entityLabel: record.subject || record.ticket_number,
                })
              }
              className="text-teal-600 hover:text-teal-700 hover:bg-teal-50 dark:hover:bg-teal-900/20"
            />
          </Tooltip>
          {canDeleteRecord(record) ? (
            <Tooltip title="حذف">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: 'حذف تیکت',
                    content: 'آیا از حذف این تیکت اطمینان دارید؟',
                    okText: 'حذف',
                    cancelText: 'لغو',
                    okButtonProps: { danger: true },
                    onOk: async () => {
                      try {
                        await ticketsApi.applyQuickAction('delete', [record.id]);
                        message.success('تیکت حذف شد');
                        fetchTickets();
                        fetchCounts();
                      } catch (error) {
                        message.error('خطا در حذف تیکت');
                      }
                    },
                  });
                }}
              />
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
  ];

  const rowSelection =
    canEditStatus || canDelete
      ? {
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          preserveSelectedRowKeys: true,
        }
      : undefined;

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="border-b border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 rounded-t-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-sky-50 dark:bg-sky-900/20 flex items-center justify-center">
                  <CustomerServiceOutlined className="text-sky-600 dark:text-sky-400 text-xl" />
                </div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-0" style={{ fontFamily: 'CustomFont' }}>
                  تیکت‌ها
                </h1>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {canManageAgent ? (
                  <Button icon={<FormOutlined />} onClick={() => navigate('/tickets/ticket-form')} size="middle">
                    فرم تیکت مشتری
                  </Button>
                ) : null}
                {canAdd ? (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => navigate('/tickets/add')}
                    size="middle"
                    className={`${PANEL_PRIMARY_BTN_CLASS} shadow-md hover:shadow-lg transition-all`}
                  >
                    تیکت جدید
                  </Button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            {selectedRowKeys.length > 0 && (canEditStatus || canDelete) ? (
              <div className={BULK_ACTION_BAR_CLASS}>
                <span className={BULK_ACTION_BAR_TEXT_CLASS} style={{ fontFamily: 'CustomFont' }}>
                  {selectedRowKeys.length} مورد انتخاب شده
                </span>
                <Dropdown menu={{ items: bulkActionMenuItems }} trigger={['click']} disabled={bulkActionLoading}>
                  <Button
                    type="primary"
                    loading={bulkActionLoading}
                    size="middle"
                    className="border-0"
                    icon={<DownOutlined />}
                    style={{ fontFamily: 'CustomFont', backgroundColor: '#f97316', color: '#fff' }}
                  >
                    عملیات گروهی
                  </Button>
                </Dropdown>
              </div>
            ) : null}

            {countStats ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
                {[
                  { key: 'total', label: 'همه', value: countStats.totalTickets, color: 'bg-slate-100 dark:bg-slate-800 border-slate-200' },
                  { key: 'open', label: 'باز', value: countStats.openTickets, color: 'bg-red-50 dark:bg-red-950/30 border-red-100' },
                  { key: 'pending', label: 'در انتظار', value: countStats.pendingTickets, color: 'bg-amber-50 dark:bg-amber-950/30 border-amber-100' },
                  { key: 'resolved', label: 'حل‌شده', value: countStats.resolvedTickets, color: 'bg-green-50 dark:bg-green-950/30 border-green-100' },
                  { key: 'closed', label: 'بسته‌شده', value: countStats.closedTickets, color: 'bg-blue-50 dark:bg-blue-950/30 border-blue-100' },
                ].map((c) => (
                  <div key={c.key} className={`rounded-xl border p-4 ${c.color} dark:border-gray-700`}>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{c.label}</div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {countLoading ? '…' : Number(c.value ?? 0).toLocaleString('fa-IR')}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="mb-6">
              <StandardFilterPanel
                searchValue={filterValues.search}
                onSearchChange={handleSearchChange}
                searchPlaceholder="جستجو موضوع، شماره تیکت..."
                quickFilters={quickFilters}
                advancedFilters={advancedFilters}
                onFilterChange={handleFilterChange}
                onResetFilters={handleResetFilters}
                totalCount={pagination.total}
                showAdvancedFilters
                showTotalCount
              />
            </div>

            {loading ? (
              <TableSkeleton rows={5} columns={8} />
            ) : (
              <Table
                columns={columns}
                dataSource={tickets}
                rowKey="id"
                rowSelection={rowSelection}
                loading={false}
                scroll={{ x: 1100 }}
                pagination={{
                  current: pagination.current_page,
                  pageSize: pagination.per_page,
                  total: pagination.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  onChange: (page, perPage) => {
                    setPagination((prev) => ({
                      ...prev,
                      current_page: page,
                      per_page: perPage,
                    }));
                  },
                  showTotal: (total, range) => (
                    <span style={{ fontFamily: 'CustomFont' }}>
                      نمایش <strong>{range[0]}</strong> تا <strong>{range[1]}</strong> از <strong>{total}</strong> تیکت
                    </span>
                  ),
                  locale: { items_per_page: 'ردیف در صفحه' },
                }}
                locale={{
                  emptyText: (
                    <div style={{ fontFamily: 'CustomFont', padding: '40px 0', color: '#999' }}>
                      {filterValues.search ? 'نتیجه‌ای یافت نشد' : 'تیکتی ثبت نشده است'}
                    </div>
                  ),
                }}
                className="custom-table"
                rowClassName="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              />
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default Tickets;
